import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { DataTableProps } from '@/types';
import { Filter, Plus, Search } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import DropdownActions from '@/components/ui/DropdownActions.tsx';
import Pagination from '@/components/ui/Pagination.tsx';

type ColumnResizeState = {
  columnIndex: number;
  startX: number;
  startWidth: number;
};

function DataTable<T extends object>({
  data,
  columns,
  actions = [],
  onSearch,
  onFilter,
  onCreate,
  searchPlaceholder = "Buscar...",
  title = "Datos",
  itemsPerPage = 5,
  paginate = true,
  showToolbar = true,
  tableStyle = 'default',
  virtualizeRows = false,
  estimatedRowHeight,
  headerRenderer,
  className = ""
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tableMaxHeight, setTableMaxHeight] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const tableColumnRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resizingColumnRef = useRef<ColumnResizeState | null>(null);
  const bodyCursorRef = useRef('');
  const bodyUserSelectRef = useRef('');
  const isCompact = tableStyle === 'compact';
  const minColumnWidth = isCompact ? 80 : 120;
  const hasResizedColumns = Object.keys(columnWidths).length > 0;
  const TABLE_BOTTOM_PADDING_PX = 16;
  const FOOTER_FALLBACK_HEIGHT_PX = isCompact ? 72 : 80;
  const TABLE_HEIGHT_SAFETY_PX = 56;
  const columnSizingKey = useMemo(
    () => columns.map((column) => `${String(column.key)}:${column.header}`).join('|'),
    [columns]
  );

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;

    return data.filter(item =>
      Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [data, searchQuery]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!paginate) {
      return filteredData;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage, paginate]);

  const totalPages = paginate ? Math.ceil(filteredData.length / itemsPerPage) : 1;

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    onSearch?.(query);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getTextAlignClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-right';
    return 'text-left';
  };

  const getJustifyClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'center') return 'justify-center';
    if (align === 'right') return 'justify-end';
    return 'justify-start';
  };

  const getCellValue = (item: T, key: keyof T | string) => {
    return String((item as Record<string, unknown>)[String(key)] || '');
  };

  const handleColumnResizeStart = (
    event: ReactPointerEvent<HTMLButtonElement>,
    columnIndex: number
  ) => {
    if (event.button !== 0) return;

    const columnWidth = tableColumnRefs.current[columnIndex]?.getBoundingClientRect().width ?? minColumnWidth;

    event.preventDefault();
    event.stopPropagation();
    resizingColumnRef.current = {
      columnIndex,
      startX: event.clientX,
      startWidth: columnWidth,
    };

    bodyCursorRef.current = document.body.style.cursor;
    bodyUserSelectRef.current = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleColumnResizeKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    columnIndex: number
  ) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentWidth = columnWidths[columnIndex]
      ?? tableColumnRefs.current[columnIndex]?.getBoundingClientRect().width
      ?? minColumnWidth;

    setColumnWidths((prev) => ({
      ...prev,
      [columnIndex]: Math.max(minColumnWidth, currentWidth + direction * 16),
    }));
  };

  const resetColumnWidth = (columnIndex: number) => {
    setColumnWidths((prev) => {
      if (!(columnIndex in prev)) return prev;

      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  const effectiveRowHeight = estimatedRowHeight ?? (isCompact ? 38 : 56);
  const rowVirtualizer = useVirtualizer({
    count: virtualizeRows ? paginatedData.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => effectiveRowHeight,
    overscan: 10,
  });
  const virtualItems = virtualizeRows ? rowVirtualizer.getVirtualItems() : [];
  const topSpacerHeight = virtualizeRows && virtualItems.length > 0 ? virtualItems[0].start : 0;
  const bottomSpacerHeight = virtualizeRows && virtualItems.length > 0
    ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;

  const updateTableMaxHeight = useCallback(() => {
    if (!tableHeaderRef.current) return;

    const headerTop = tableHeaderRef.current.getBoundingClientRect().top;
    const contentBottom = contentContainerRef.current
      ? contentContainerRef.current.getBoundingClientRect().bottom
      : window.innerHeight;
    const hasFooter = paginate && totalPages > 1;
    const footerHeight = hasFooter
      ? (footerRef.current?.getBoundingClientRect().height ?? FOOTER_FALLBACK_HEIGHT_PX)
      : 0;
    const nextHeight = Math.max(
      180,
      Math.floor(contentBottom - headerTop - footerHeight - TABLE_BOTTOM_PADDING_PX - TABLE_HEIGHT_SAFETY_PX)
    );

    setTableMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
  }, [FOOTER_FALLBACK_HEIGHT_PX, paginate, totalPages]);

  useEffect(() => {
    if (tableHeaderRef.current) {
      contentContainerRef.current = tableHeaderRef.current.closest('.app-content') as HTMLElement | null;
    }
    updateTableMaxHeight();
  }, [updateTableMaxHeight, isCompact, showToolbar, columns.length, actions.length, paginatedData.length]);

  useEffect(() => {
    window.addEventListener('resize', updateTableMaxHeight);
    return () => {
      window.removeEventListener('resize', updateTableMaxHeight);
    };
  }, [updateTableMaxHeight]);

  useEffect(() => {
    setColumnWidths({});
  }, [columnSizingKey]);

  useEffect(() => {
    const stopResizing = () => {
      if (!resizingColumnRef.current) return;

      resizingColumnRef.current = null;
      document.body.style.cursor = bodyCursorRef.current;
      document.body.style.userSelect = bodyUserSelectRef.current;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizingColumnRef.current;
      if (!resizeState) return;

      const nextWidth = Math.max(
        minColumnWidth,
        resizeState.startWidth + event.clientX - resizeState.startX
      );

      setColumnWidths((prev) => (
        prev[resizeState.columnIndex] === nextWidth
          ? prev
          : { ...prev, [resizeState.columnIndex]: nextWidth }
      ));
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);

    return () => {
      stopResizing();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
    };
  }, [minColumnWidth]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      updateTableMaxHeight();
    });

    if (tableHeaderRef.current) observer.observe(tableHeaderRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    if (contentContainerRef.current) observer.observe(contentContainerRef.current);

    return () => observer.disconnect();
  }, [updateTableMaxHeight, totalPages, paginatedData.length]);

  return (
    <div className={`bg-white shadow border border-gray-200 ${className}`}>
      {/* Table Header */}
      {headerRenderer ? (
        <div className="relative z-30 border-b border-gray-200">
          {headerRenderer({
            title,
            searchQuery,
            searchPlaceholder,
            totalCount: data.length,
            filteredCount: filteredData.length,
            onSearchChange: handleSearch,
            onFilter,
            onCreate,
            isCompact,
          })}
        </div>
      ) : showToolbar && (
        <div className={`${isCompact ? 'px-4 py-3' : 'px-6 py-4'} border-b border-gray-200 relative z-30`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`${isCompact ? 'py-1 text-base' : 'py-2'} pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-64`}
                />
              </div>

              {/* Filter Button */}
              {onFilter && (
                <button
                  onClick={onFilter}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  type="button"
                >
                  <Filter size={16} className="mr-2" />
                  Filtros
                </button>
              )}

              {/* Create Button */}
              {onCreate && (
                <button
                  onClick={onCreate}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  type="button"
                >
                  <Plus size={16} className="mr-2" />
                  Crear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="relative z-20 overflow-visible">
        <div
          ref={scrollContainerRef}
          className="overflow-auto"
          style={tableMaxHeight ? { maxHeight: `${tableMaxHeight}px` } : undefined}
        >
          <table className={`min-w-full ${hasResizedColumns ? 'table-fixed' : ''}`}>
            <colgroup>
              {columns.map((_, index) => (
                <col
                  key={index}
                  style={columnWidths[index] ? { width: `${columnWidths[index]}px` } : undefined}
                />
              ))}
              {actions.length > 0 && <col />}
            </colgroup>
            <thead ref={tableHeaderRef} className="bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    ref={(element) => {
                      tableColumnRefs.current[index] = element;
                    }}
                    className={`${isCompact ? 'px-3 py-1 text-[11px]' : 'px-6 py-3 text-xs'} sticky top-0 z-20 border-b border-gray-200 bg-gray-50 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] ${getTextAlignClass(column.align)} font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}
                  >
                    <div className={`flex items-center ${getJustifyClass(column.align)} overflow-hidden`}>
                      <span>{column.header}</span>
                    </div>
                    <button
                      type="button"
                      aria-label={`Redimensionar columna ${column.header}`}
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none bg-transparent hover:bg-indigo-300/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                      onPointerDown={(event) => handleColumnResizeStart(event, index)}
                      onKeyDown={(event) => handleColumnResizeKeyDown(event, index)}
                      onDoubleClick={() => resetColumnWidth(index)}
                    >
                      <span className="sr-only">Redimensionar</span>
                    </button>
                  </th>
                ))}
                {actions.length > 0 && (
                  <th className={`${isCompact ? 'px-3 py-1 text-[11px]' : 'px-6 py-3 text-xs'} sticky top-0 z-20 border-b border-gray-200 bg-gray-50 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] text-right font-medium text-gray-500 uppercase tracking-wider`}>
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {virtualizeRows && topSpacerHeight > 0 && (
                <tr>
                  <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)} style={{ height: `${topSpacerHeight}px`, padding: 0 }} />
                </tr>
              )}
              {(virtualizeRows ? virtualItems.map((virtualRow) => ({
                item: paginatedData[virtualRow.index],
                rowIndex: virtualRow.index,
                key: virtualRow.key,
              })) : paginatedData.map((item, rowIndex) => ({ item, rowIndex, key: rowIndex }))).map(({ item, rowIndex, key }) => (
                <tr key={key} className="hover:bg-gray-50">
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className={`${isCompact ? 'px-3 py-1 text-sm' : 'px-6 py-4 text-sm'} ${getTextAlignClass(column.align)} whitespace-nowrap ${column.className || ''}`}>
                      {column.accessor
                        ? column.accessor(item, rowIndex)
                        : getCellValue(item, column.key)
                      }
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className={`${isCompact ? 'px-3 py-1' : 'px-6 py-4'} whitespace-nowrap text-right text-sm font-medium`}>
                      <DropdownActions actions={actions} item={item} />
                    </td>
                  )}
                </tr>
              ))}
              {virtualizeRows && bottomSpacerHeight > 0 && (
                <tr>
                  <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)} style={{ height: `${bottomSpacerHeight}px`, padding: 0 }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {paginatedData.length === 0 && (
        <div className={`${isCompact ? 'py-10' : 'py-12'} text-center`}>
          <div className="text-gray-400 mb-4">
            <Search size={48} className="mx-auto" />
          </div>
          <p className="text-gray-500 text-lg">No se encontraron resultados</p>
          <p className="text-gray-400 text-sm mt-2">
            {searchQuery ? 'Intenta con otros términos de búsqueda' : 'No hay datos disponibles'}
          </p>
        </div>
      )}

      {/* Footer */}
      {paginate && totalPages > 1 && (
        <div ref={footerRef} className="relative z-10">
          <Pagination
            page={currentPage - 1}
            totalPages={totalPages}
            onPageChange={(page) => handlePageChange(page + 1)}
            size={itemsPerPage}
            totalElements={filteredData.length}
          />
        </div>
      )}
      {!paginate && filteredData.length > 0 && (
        <div ref={footerRef} className="flex flex-col gap-2 border-t border-gray-200 bg-white px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full items-center justify-center text-center text-sm text-gray-500 whitespace-nowrap sm:w-auto sm:justify-start sm:text-left">
            Mostrando {filteredData.length} resultados
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
